import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons/faCirclePlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Greeting } from 'us-react-components/dist/Greeting/Greeting';
import {
  axios,
  Booking,
  CreditTransferResponse,
  Job,
  PartnerCreditBalance,
  PartnerCreditService,
  Referral,
  ReferralOffer,
  SavedSearch,
  UserGeocode }
  from 'us-web-services';
import { Button, ButtonType, CariloopButton, KinsideButton } from '../../components/Button';
import { ReferralOfferReceived } from '../../components/ReferralOfferReceived';
import { useDependencies } from '../../dependencies';
import { DashboardBookings } from '../../components/Parent/Dashboard/components/bookings/DashboardBookings';
import { DashboardJobPosts } from '../../components/Parent/Dashboard/components/jobPosts/JobPosts';
import { JobPostsNullState } from '../../components/Parent/Dashboard/components/jobPosts/JobPostsNullState';
import { AgencyCareWidget } from '../../components/Parent/Dashboard/components/widgets/AgencyCareWidget';
import { CareBenefits } from '../../components/Parent/Dashboard/components/widgets/CareBenefits';
import { OutOfNetworkReimbursement } from '../../components/Parent/Dashboard/components/widgets/OutOfNetworkReimbursement';
import { BringYourOwnProvider } from '../../components/Parent/Dashboard/components/widgets/BringYourOwnProvider';
import { CreditlessPartnerWidget } from '../../components/Parent/Dashboard/components/widgets/CreditlessPartnerWidget';
import { ExpandCircleOfTrust } from '../../components/Parent/Dashboard/components/widgets/ExpandCircleOfTrust';
import { FacebookWidget } from '../../components/Parent/Dashboard/components/widgets/FacebookWidget';
import { PartnerCreditWidget, PartnerName } from '../../components/Parent/Dashboard/components/widgets/PartnerCreditWidget';
import { SafetyTrust } from '../../components/Parent/Dashboard/components/widgets/SafetyTrust';
import { SavedSearchWidget } from '../../components/Parent/Dashboard/components/widgets/SavedSearchWidget';

const OUTSCHOOL_LOGO_URL = 'https://assets.urbansitter.com/static/img/logos/outschool.svg';
const ACTIVITYHERO_LOGO_URL = 'https://assets.urbansitter.com/static/img/logos/activityhero.svg';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  activeBookings: Booking[];
  activeJobs: Job[];
  geocode: UserGeocode;
  handlePostAJob(): void;
  isAgencyCareAvailable: boolean;
  openJobCount: number;
  parentSavedSearches: SavedSearch[];
  referral?: Referral;
  referralOffer?: ReferralOffer;
}

const SideBar = (props: Props): JSX.Element => {
  const { activeBookings, activeJobs, geocode, handlePostAJob, isAgencyCareAvailable, openJobCount, parentSavedSearches, referral, referralOffer } = props;
  const { stores } = useDependencies();
  const { hasActivityHeroPartnership, hasCariloopPartnership, hasKinsidePartnership, hasOutschoolPartnership, hasOonBenefit, hasByopBenefit, isActiveEmployee } = stores.parent;
  const { user } = stores.user;

  const { t } = useTranslation();

  const [outschoolBalance, setOutschoolBalance] = useState<PartnerCreditBalance>();
  const [outschoolSuccessfulTransfer, setOutschoolSuccessfulTransfer] = useState(false);
  const [activityHeroBalance, setActivityHeroBalance] = useState<PartnerCreditBalance>();
  const [activityHeroSuccessfulTransfer, setActivityHeroSuccessfulTransfer] = useState(false);

  const balanceActions: Record<PartnerName, React.Dispatch<React.SetStateAction<PartnerCreditBalance | undefined>>> = {
    Outschool: setOutschoolBalance,
    ActivityHero: setActivityHeroBalance,
  };

  const transferActions: Record<PartnerName, React.Dispatch<React.SetStateAction<boolean>>> = {
    Outschool: setOutschoolSuccessfulTransfer,
    ActivityHero: setActivityHeroSuccessfulTransfer,
  };

  const getBalanceAndRender = async (partner: PartnerName): Promise<void> => {
    const response = (await PartnerCreditService.get(partner, user.id || 0)).data;

    balanceActions[partner](response.data);
  }

  const updateBalances = async (): Promise<void> => {
    await getBalanceAndRender('Outschool');
    await getBalanceAndRender('ActivityHero');
  }

  const handleTransfer = async (partner: PartnerName, amount: number): Promise<CreditTransferResponse> => {
    const data = {
      userId: user.id,
      amount,
    };

    try {
      await PartnerCreditService.create(partner, data);

      toast.success(t('dashboard.widgets.partnerCredit.transferSuccessful'), { icon: '🎉' });

      transferActions[partner](true);

      await updateBalances();

      return { success: true };
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data.errors) {
        toast.error(e.response.data.errors[0].message);
        transferActions[partner](false);
        return { success: false, error: e.response.data.errors[0] };
      }

      return { success: false };
    }
  }

  useEffect(() => {
    const loadBalances = async (): Promise<void> => {
      await updateBalances();
    };

    loadBalances();
  }, []);

  return (
      <div className='md:1/3 lg:w-80 md:mb-20'>
        <div className='flex mb-5'>
          <Greeting
            firstName={user.firstName || ''}
            description={t('components.greeting.findTheCare')}
            t={t}
          />
        </div>
        {referral?.invitedUserId && referralOffer?.content &&
          <div className='flex mb-5'>
            <ReferralOfferReceived referral={referral} referralOffer={referralOffer} />
          </div>
        }
        <div className='mb-5'>
          <div className='flex justify-between'>
            <div className='inline-flex font-bold pt-1 mb-2.5'>{t('dashboard.widgets.myJobPosts.heading')}</div>
            { activeJobs.length > 0 && (
              <div className='inline-flex text-sapphire'>
                <Button buttonType={ButtonType.tertiary} onClick={handlePostAJob} className='text-sm'>
                  <FontAwesomeIcon icon={faCirclePlus} className='mr-1 mt-1' />
                  {t('dashboard.widgets.myJobPosts.postAnotherJob')}
                </Button>
              </div>
            )}
          </div>
          { activeJobs.length > 0
            ? <DashboardJobPosts jobs={activeJobs} openJobCount={openJobCount} />
            : <JobPostsNullState handlePostAJob={handlePostAJob} /> }
        </div>
        {parentSavedSearches.length > 0 && (
          <div className='mb-5'>
            <div className='block font-bold mb-2.5'>{t('dashboard.widgets.savedSearch.heading')}</div>
            <SavedSearchWidget parentSavedSearches={parentSavedSearches} />
          </div>
        )}
        {activeBookings.length > 0 && (
          <div className='mb-5'>
            <div className='block font-bold mb-2.5'>{t('dashboard.widgets.bookings.label')}</div>
            <DashboardBookings bookings={activeBookings} />
          </div>
        )}
        <CareBenefits />
        {hasOonBenefit && (
          <div className='mb-5'>
            <OutOfNetworkReimbursement />
          </div>
        )}
        {hasOutschoolPartnership && (
          <div className='mb-5'>
            <PartnerCreditWidget
              partner='Outschool'
              partnerLogo={OUTSCHOOL_LOGO_URL}
              balance={outschoolBalance}
              successfulTransfer={outschoolSuccessfulTransfer}
              handleTransfer={handleTransfer}
            />
          </div>
        )}
        {hasActivityHeroPartnership && (
          <div className='mb-5'>
            <PartnerCreditWidget
              partner='ActivityHero'
              partnerLogo={ACTIVITYHERO_LOGO_URL}
              balance={activityHeroBalance}
              successfulTransfer={activityHeroSuccessfulTransfer}
              handleTransfer={handleTransfer}
            />
          </div>
        )}
        {!hasActivityHeroPartnership && isActiveEmployee && (
          <div className='mb-5'>
            <CreditlessPartnerWidget
              partnerLogo={ACTIVITYHERO_LOGO_URL}
              partnerUrl='https://www.activityhero.com/?utm_source=urbansitter&utm_medium=partner&utm_campaign=carecredits'
            />
          </div>
        )}
        {hasByopBenefit && (
          <div className='mb-5'>
            <BringYourOwnProvider />
          </div>
        )}
        {isAgencyCareAvailable && (
          <div className='my-5 hidden md:flex'>
            <AgencyCareWidget city={geocode.city} state={geocode.state} zip={geocode.zip} />
          </div>
        )}
        {hasKinsidePartnership && (
          <div className='mb-5 px-4 py-3 place-content-center rounded-2xl border-2 w-full'>
            <div className='text-sm mt-2 mb-3'>{t('dashboard.widgets.kinside')}</div>
            <div><KinsideButton /></div>
          </div>
        )}
        {hasCariloopPartnership && (
          <div className='px-4 py-3 mb-5 place-content-center rounded-2xl border-2'>
            <div className='block font-bold mb-2.5'>{t('dashboard.widgets.cariloop')}</div>
            <CariloopButton />
          </div>
        )}
        <div className='mb-5'>
          <ExpandCircleOfTrust />
        </div>
        <div className='hidden md:flex lg:flex xl:hidden'>
          <SafetyTrust />
        </div>
        {!user?.facebookId && (
          <div className='my-5 hidden md:flex'>
            <FacebookWidget />
          </div>
        )}
      </div>
  );
};

export { SideBar };
